import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authenticatedFetch } from "../../utils/api";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Company {
  id: number;
  name: string;
}

const received_noteDetailSchema = z.object({
  counts: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Counts is required" }))
    .refine((val) => Number.isInteger(val), "Counts must be an integer")
    .refine((val) => val > 0, "Counts must be greater than 0"),
  number_of_bags: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Number of bags is required" }))
    .refine((val) => Number.isInteger(val), "Number of bags must be an integer")
    .refine((val) => val > 0, "Number of bags must be greater than 0"),
  weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Weight is required" }))
    .refine((val) => val > 0, "Weight must be greater than 0"),
  mill: z.string().optional(),
  color: z.string().optional(),
});

const received_noteSchema = z.object({
  company_id: z.number().int().optional().nullable(),
  company_name: z.string().optional(),
  representative_name: z.string().optional(),
  representative_phone: z
    .number()
    .int("Phone number must be an integer")
    .optional()
    .nullable(),
  received_note_details_attributes: z
    .array(received_noteDetailSchema)
    .min(1, "At least one received_note detail is required"),
}).refine((data) => data.company_id || data.company_name, {
  message: "Either select a company or enter a new company name",
  path: ["company_id"],
});

type ReceivedNoteFormData = z.infer<typeof received_noteSchema>;

export default function CreateReceivedNote() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);

  const { control, handleSubmit, watch, setValue } = useForm<ReceivedNoteFormData>({
    resolver: zodResolver(received_noteSchema),
    defaultValues: {
      company_id: undefined,
      company_name: "",
      representative_name: "",
      representative_phone: undefined,
      received_note_details_attributes: [
        { counts: "" as any, number_of_bags: "" as any, weight: "" as any, mill: "", color: "" },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "received_note_details_attributes",
  });

  const received_noteDetails = watch("received_note_details_attributes");

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }

      const data = await response.json();
      setCompanies(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const onSubmit = async (data: ReceivedNoteFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/received_notes`, {
        method: "POST",
        body: JSON.stringify({ received_note: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      navigate("/received-notes");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const calculateTotalWeight = () => {
    return received_noteDetails
      ?.reduce((sum, detail) => {
        const bags = detail?.number_of_bags || 0;
        const weight = detail?.weight || 0;
        return sum + bags * weight;
      }, 0)
      .toFixed(2);
  };

  const calculateTotalBags = () => {
    return received_noteDetails?.reduce(
      (sum, detail) => sum + (detail?.number_of_bags || 0),
      0
    );
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
        {/* Header */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          spacing={2} 
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <IconButton 
              onClick={() => navigate("/received-notes")} 
              color="primary" 
              size={isMobile ? "small" : "medium"}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h6" : "h5"} component="h2" sx={{ flexGrow: 1 }}>
              {t('receivedNotes.createTitle')}
            </Typography>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('basicInformation')}</Typography>
              <Stack spacing={2}>
                <Controller
                  name="company_id"
                  control={control}
                  render={({ field: { onChange, value }, fieldState }) => {
                    const selectedCompany = companies.find(c => c.id === value);
                    const companyName = watch('company_name');
                    
                    return (
                      <Autocomplete
                        options={companies}
                        getOptionLabel={(option) => 
                          typeof option === 'string' ? option : option.name
                        }
                        value={selectedCompany || null}
                        inputValue={selectedCompany ? selectedCompany.name : (companyName || '')}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            if (typeof newValue === 'string') {
                              onChange(null);
                              setValue('company_name', newValue);
                            } else {
                              onChange(newValue.id);
                              setValue('company_name', '');
                            }
                          } else {
                            onChange(null);
                            setValue('company_name', '');
                          }
                        }}
                        onInputChange={(_, newInputValue, reason) => {
                          if (reason === 'input') {
                            const matchedCompany = companies.find(c => c.name === newInputValue);
                            if (matchedCompany) {
                              onChange(matchedCompany.id);
                              setValue('company_name', '');
                            } else {
                              setValue('company_name', newInputValue);
                              onChange(null);
                            }
                          }
                        }}
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        size={isMobile ? "small" : "medium"}
                        filterOptions={(options, params) => {
                          const filtered = options.filter((option) =>
                            option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                          );
                          return filtered;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${t('receivedNotes.company')} *`}
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error?.message || 
                              t('receivedNotes.selectCompanyOrEnterNew')
                            }
                          />
                        )}
                      />
                    );
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="representative_name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t('representativeName')}
                          placeholder={t('optional')}
                          fullWidth
                          size={isMobile ? "small" : "medium"}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="representative_phone"
                      control={control}
                      render={({
                        field: { onChange, value, ...field },
                        fieldState,
                      }) => (
                        <TextField
                          {...field}
                          value={value || ""}
                          onChange={(e) =>
                            onChange(e.target.value ? Number(e.target.value) : undefined)
                          }
                          label={t('representativePhone')}
                          placeholder={t('optional')}
                          type="tel"
                          fullWidth
                          size={isMobile ? "small" : "medium"}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          {/* Received Note Details Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">{t('receivedNotes.details')}</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    append({
                      counts: "" as any,
                      number_of_bags: "" as any,
                      weight: "" as any,
                      mill: "",
                      color: "",
                    })
                  }
                  fullWidth={isMobile}
                >
                  {t('addDetail')}
                </Button>
              </Stack>

              {isMobile ? (
                /* Mobile Card-Based Layout */
                <Stack spacing={2}>
                  {fields.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {t('noDetailsAdded')}
                      </Typography>
                    </Paper>
                  ) : (
                    fields.map((field, index) => {
                      const detail = received_noteDetails[index];
                      const rowTotal = detail?.number_of_bags && detail?.weight 
                        ? (detail.number_of_bags * detail.weight).toFixed(2)
                        : "0.00";
                      
                      return (
                        <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {t('receivedNotes.detailNumber')}{index + 1}
                              </Typography>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => remove(index)}
                                disabled={fields.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Controller
                                  name={`received_note_details_attributes.${index}.counts`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('receivedNotes.counts')} *
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(e.target.value ? Number(e.target.value) : undefined)
                                        }
                                        type="number"
                                        size="small"
                                        inputProps={{ step: "1" }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        placeholder="e.g. 40"
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`received_note_details_attributes.${index}.number_of_bags`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('receivedNotes.numberOfBags')} *
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(e.target.value ? Number(e.target.value) : undefined)
                                        }
                                        type="number"
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        placeholder="e.g. 10"
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Controller
                                  name={`received_note_details_attributes.${index}.weight`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('receivedNotes.weightPerBag')} *
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(e.target.value ? Number(e.target.value) : undefined)
                                        }
                                        type="number"
                                        size="small"
                                        inputProps={{ step: "0.01" }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        placeholder="e.g. 50"
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`received_note_details_attributes.${index}.mill`}
                                  control={control}
                                  render={({ field: fieldProps, fieldState }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('receivedNotes.mill')}
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        placeholder={t('optional')}
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`received_note_details_attributes.${index}.color`}
                                  control={control}
                                  render={({ field: fieldProps, fieldState }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        {t('receivedNotes.color')}
                                      </Typography>
                                      <TextField
                                        {...fieldProps}
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        placeholder={t('optional')}
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                            </Grid>

                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1.5, 
                                bgcolor: 'primary.50', 
                                borderColor: 'primary.200',
                                borderRadius: 1
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {t('receivedNotes.totalWeight')}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                  {rowTotal} kg
                                </Typography>
                              </Stack>
                            </Paper>
                          </Stack>
                        </Paper>
                      );
                    })
                  )}

                  {fields.length > 0 && (
                    <>
                      <Divider />
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" fontWeight="bold">{t('grandTotal')}:</Typography>
                          <Stack direction="row" spacing={3}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.bags')}</Typography>
                              <Typography variant="body2" fontWeight="bold">{calculateTotalBags()}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.weight')}</Typography>
                              <Typography variant="body2" fontWeight="bold">{calculateTotalWeight()} kg</Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </Paper>
                    </>
                  )}
                </Stack>
              ) : (
                /* Desktop Table Layout */
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('receivedNotes.counts')}</TableCell>
                        <TableCell>{t('receivedNotes.numberOfBags')}</TableCell>
                        <TableCell>{t('receivedNotes.weightPerBag')}</TableCell>
                        <TableCell>{t('receivedNotes.totalWeightKg')}</TableCell>
                        <TableCell>{t('receivedNotes.mill')}</TableCell>
                        <TableCell>{t('receivedNotes.color')}</TableCell>
                        <TableCell width={60}>{t('action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => {
                        const detail = received_noteDetails[index];
                        const rowTotal = detail?.number_of_bags && detail?.weight 
                          ? (detail.number_of_bags * detail.weight).toFixed(2)
                          : "0.00";
                        
                        return (
                          <TableRow key={field.id}>
                            <TableCell>
                              <Controller
                                name={`received_note_details_attributes.${index}.counts`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...fieldProps },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...fieldProps}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    inputProps={{ step: "1" }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`received_note_details_attributes.${index}.number_of_bags`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...fieldProps },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...fieldProps}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`received_note_details_attributes.${index}.weight`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...fieldProps },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...fieldProps}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    inputProps={{ step: "0.01" }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {rowTotal}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`received_note_details_attributes.${index}.mill`}
                                control={control}
                                render={({ field: fieldProps, fieldState }) => (
                                  <TextField
                                    {...fieldProps}
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`received_note_details_attributes.${index}.color`}
                                control={control}
                                render={({ field: fieldProps, fieldState }) => (
                                  <TextField
                                    {...fieldProps}
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => remove(index)}
                                disabled={fields.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell>
                          <strong>{t('total')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{calculateTotalBags()}</strong>
                        </TableCell>
                        <TableCell colSpan={1}></TableCell>
                        <TableCell>
                          <strong>{calculateTotalWeight()} kg</strong>
                        </TableCell>
                        <TableCell colSpan={3}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate("/received-notes")}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
            >
              {t('receivedNotes.createTitle')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
}

